/**
 * wordDisplay.js - Wort-Anzeige mit mehreren Buchstaben nebeneinander
 *
 * Zeigt ein ganzes Wort (z.B. "OPEN121") mit allen Buchstaben gleichzeitig an.
 * Jeder Buchstabe hat eigene Haare, aber weniger pro Buchstabe für Performance.
 *
 * Unterstützt: A-Z, 0-9
 */
import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { scene, adjustCameraForWidth } from "./scene.js";
import { settings, DAMPING, AMOUNT } from "./settings.js";
import { mapY, rotationImpulse, decayRotationImpulse } from "./input.js";

// Shader importieren
import vertexShader from "../shaders/hair.vert";
import fragmentShader from "../shaders/hair.frag";

// Word-Container (Group für alle Buchstaben)
let wordGroup = null;

// Buchstaben-Daten
const letterData = {}; // { "O": { mesh, hairMesh, hairGeometry, uniforms, ... }, ... }

// State
let isLoaded = false;
let loadedCount = 0;
let currentWord = "";
let letterInstances = []; // Array von { letter, mesh, hairMesh, xOffset, ... }

// Settings
let HAIR_PER_LETTER = 8000; // Weniger Haare pro Buchstabe für Performance
const SEGMENTS_PER_HAIR = 8;
let LETTER_SPACING = 0.35; // Abstand zwischen Buchstaben
let LETTER_SCALE = 0.35; // Skalierung der Buchstaben
let LINE_HEIGHT = 0.45; // Abstand zwischen Zeilen (für mehrzeilige Wörter)

// Haar-Settings
let HAIR_ENABLED = true; // Haare an/aus
let HAIR_LENGTH = 0.08;
let HAIR_COLOR_ROOT = "#3D2314";
let HAIR_COLOR_TIP = "#5C3A21";
let BEND_STRENGTH = 0.4;
let GRAVITY_STRENGTH = 0.12;

// Wortbreite (für responsive Kamera)
let wordWidth = 0;

// Callback
let onLoadCallback = null;

/**
 * Gibt den Dateipfad für einen Buchstaben zurück
 */
function getLetterPath(letter) {
  if (/[0-9]/.test(letter)) {
    return `models/letters/num_${letter}.glb`;
  }
  return `models/letters/${letter}.glb`;
}

/**
 * Parst Text zu Array von Zeilen, jede Zeile ist Array von Buchstaben
 * Unterstützt | als Zeilenumbruch (z.B. "OPEN|121" wird zu zwei Zeilen)
 */
function parseTextToLines(text) {
  const upperText = text.toUpperCase();
  // Split bei | für Zeilenumbruch
  const lines = upperText.split("|");

  return lines.map(line => {
    const letters = [];
    for (const char of line) {
      if (/[A-Z0-9]/.test(char)) {
        letters.push(char);
      }
    }
    return letters;
  }).filter(line => line.length > 0); // Leere Zeilen entfernen
}

/**
 * Parst Text zu flachem Array von Buchstaben (für Laden)
 */
function parseText(text) {
  const lines = parseTextToLines(text);
  return lines.flat();
}

/**
 * Lädt alle Buchstaben für ein Wort
 */
export function loadWord(word, callback) {
  const loader = new GLTFLoader();
  onLoadCallback = callback;
  currentWord = word;

  // Settings holen
  HAIR_ENABLED = settings.wordDisplayHair !== false; // Default true
  HAIR_PER_LETTER = settings.wordDisplayHairPerLetter || 8000;
  HAIR_COLOR_ROOT = settings.wordDisplayHairColorRoot || "#3D2314";
  HAIR_COLOR_TIP = settings.wordDisplayHairColorTip || "#5C3A21";
  HAIR_LENGTH = settings.wordDisplayHairLength || 0.08;
  BEND_STRENGTH = settings.wordDisplayBendStrength || 0.4;
  GRAVITY_STRENGTH = settings.wordDisplayGravityStrength || 0.12;
  LETTER_SCALE = settings.wordDisplayLetterScale || 0.35;
  LETTER_SPACING = settings.wordDisplayLetterSpacing || 0.35;
  LINE_HEIGHT = settings.wordDisplayLineHeight || 0.45;

  const letters = parseText(word);
  const uniqueLetters = [...new Set(letters)];

  console.log(`[WordDisplay] Loading word: "${word}"`);
  console.log(`[WordDisplay] Unique letters: ${uniqueLetters.join(", ")}`);
  console.log(`[WordDisplay] Hair per letter: ${HAIR_PER_LETTER}`);
  console.log(`[WordDisplay] Total hairs: ${letters.length * HAIR_PER_LETTER}`);

  loadedCount = 0;
  isLoaded = false;

  // Word-Group erstellen
  wordGroup = new THREE.Group();
  wordGroup.name = "WordDisplayGroup";
  scene.add(wordGroup);

  // Alle einzigartigen Buchstaben laden
  uniqueLetters.forEach((letter) => {
    if (letterData[letter]) {
      loadedCount++;
      checkAllLoaded(letters);
      return;
    }

    const path = getLetterPath(letter);

    loader.load(
      path,
      (gltf) => {
        console.log(`[WordDisplay] Loaded: ${letter}`);

        let mesh = null;
        gltf.scene.traverse((child) => {
          if (child.isMesh && !mesh) {
            mesh = child;
          }
        });

        if (mesh) {
          // Punktwolke extrahieren (mit hairToVertexMap)
          const { positions, normals, hairToVertexMap } = samplePointsFromMesh(mesh, HAIR_PER_LETTER);

          // Mesh klonen und transformieren
          const clonedGeom = mesh.geometry.clone();
          transformGeometry(clonedGeom);

          // Original-Positionen speichern (für Deformation-Sync)
          const positionClone = new Float32Array(clonedGeom.attributes.position.array);
          const normalClone = clonedGeom.attributes.normal ? new Float32Array(clonedGeom.attributes.normal.array) : null;

          letterData[letter] = {
            positions,
            normals,
            geometry: clonedGeom,
            hairToVertexMap,
            positionClone,
            normalClone,
            vertexCount: clonedGeom.attributes.position.count,
          };
        } else {
          console.warn(`[WordDisplay] No mesh in ${letter}.glb`);
          letterData[letter] = {
            positions: new Float32Array(HAIR_PER_LETTER * 3),
            normals: new Float32Array(HAIR_PER_LETTER * 3),
            geometry: null,
            hairToVertexMap: new Int32Array(HAIR_PER_LETTER),
            positionClone: null,
            normalClone: null,
            vertexCount: 0,
          };
        }

        loadedCount++;
        checkAllLoaded(letters);
      },
      null,
      (error) => {
        console.error(`[WordDisplay] Error loading ${letter}:`, error);
        letterData[letter] = {
          positions: new Float32Array(HAIR_PER_LETTER * 3),
          normals: new Float32Array(HAIR_PER_LETTER * 3),
          geometry: null,
          hairToVertexMap: new Int32Array(HAIR_PER_LETTER),
          positionClone: null,
          normalClone: null,
          vertexCount: 0,
        };
        loadedCount++;
        checkAllLoaded(letters);
      }
    );
  });
}

/**
 * Transformiert Geometrie von Blender zu Three.js Koordinaten
 */
function transformGeometry(geometry) {
  const posArray = geometry.attributes.position.array;
  const normArray = geometry.attributes.normal ? geometry.attributes.normal.array : null;
  const vertCount = geometry.attributes.position.count;

  for (let v = 0; v < vertCount; v++) {
    const x = posArray[v * 3];
    const y = posArray[v * 3 + 1];
    const z = posArray[v * 3 + 2];

    posArray[v * 3] = x;
    posArray[v * 3 + 1] = -z;
    posArray[v * 3 + 2] = y;

    if (normArray) {
      const nx = normArray[v * 3];
      const ny = normArray[v * 3 + 1];
      const nz = normArray[v * 3 + 2];
      normArray[v * 3] = nx;
      normArray[v * 3 + 1] = -nz;
      normArray[v * 3 + 2] = ny;
    }
  }

  geometry.attributes.position.needsUpdate = true;
  if (normArray) geometry.attributes.normal.needsUpdate = true;
  geometry.computeVertexNormals();
}

/**
 * Prüft ob alle Buchstaben geladen sind
 */
function checkAllLoaded(letters) {
  const uniqueCount = [...new Set(letters)].length;
  if (loadedCount >= uniqueCount) {
    console.log("[WordDisplay] All letters loaded!");
    createWordDisplay(letters);
    isLoaded = true;
    if (onLoadCallback) {
      onLoadCallback(wordGroup);
    }
  }
}

/**
 * Erstellt die Wort-Anzeige mit allen Buchstaben (unterstützt mehrzeilige Texte)
 */
function createWordDisplay(letters) {
  // Parse zu Zeilen für mehrzeilige Unterstützung
  const lines = parseTextToLines(currentWord);
  const numLines = lines.length;

  // Berechne maximale Zeilenbreite (für Kamera)
  let maxLineWidth = 0;
  lines.forEach(line => {
    const lineWidth = (line.length - 1) * LETTER_SPACING + LETTER_SCALE * 0.8;
    if (lineWidth > maxLineWidth) maxLineWidth = lineWidth;
  });

  // Gesamthöhe berechnen
  const totalHeight = (numLines - 1) * LINE_HEIGHT;

  // Wortbreite speichern (für responsive Kamera)
  wordWidth = Math.max(maxLineWidth, totalHeight * 1.5); // Berücksichtige auch Höhe

  // Kamera anpassen
  adjustCameraForWidth(wordWidth);

  letterInstances = [];
  let globalIndex = 0;

  // Jede Zeile verarbeiten
  lines.forEach((line, lineIndex) => {
    // Y-Position für diese Zeile (von oben nach unten)
    const yOffset = (totalHeight / 2) - lineIndex * LINE_HEIGHT;

    // X-Zentrierung für diese Zeile
    const lineWidth = (line.length - 1) * LETTER_SPACING;
    const startX = -lineWidth / 2;

    // Jeden Buchstaben in dieser Zeile
    line.forEach((letter, charIndex) => {
      const data = letterData[letter];
      if (!data) return;

      const xOffset = startX + charIndex * LETTER_SPACING;

      // Buchstaben-Mesh erstellen (optional sichtbar)
      let letterMesh = null;
      if (data.geometry && settings.wordDisplayShowMesh !== false) {
        letterMesh = new THREE.Mesh(
          data.geometry.clone(),
          new THREE.MeshStandardMaterial({
            color: new THREE.Color(settings.wordDisplayMeshColor || HAIR_COLOR_ROOT),
            metalness: 0.3,
            roughness: 0.7,
          })
        );
        letterMesh.scale.set(LETTER_SCALE, LETTER_SCALE, LETTER_SCALE);
        letterMesh.position.x = xOffset;
        letterMesh.position.y = yOffset;
        letterMesh.name = `Letter_${letter}_${globalIndex}`;
        wordGroup.add(letterMesh);
      }

      // Haar-System für diesen Buchstaben erstellen (nur wenn Haare aktiviert)
      let hairSystem = null;
      if (HAIR_ENABLED) {
        hairSystem = createHairSystemForLetter(data, xOffset, yOffset);
      }

      letterInstances.push({
        letter,
        index: globalIndex,
        xOffset,
        yOffset,
        mesh: letterMesh,
        hairMesh: hairSystem ? hairSystem.mesh : null,
        hairGeometry: hairSystem ? hairSystem.geometry : null,
        uniforms: hairSystem ? hairSystem.uniforms : null,
        rootPositionAttr: hairSystem ? hairSystem.rootPositionAttr : null,
        rootNormalAttr: hairSystem ? hairSystem.rootNormalAttr : null,
        velocityAttr: hairSystem ? hairSystem.velocityAttr : null,
        positions: data.positions,
        normals: data.normals,
        hairToVertexMap: data.hairToVertexMap,
        positionClone: data.positionClone,
        normalClone: data.normalClone,
        vertexCount: data.vertexCount,
        hairVelocities: HAIR_ENABLED ? new Float32Array(HAIR_PER_LETTER * 3) : null,
        lastRootPositions: HAIR_ENABLED ? new Float32Array(data.positions) : null,
        // Array für Vertex-Deformationen (wird in updateWordDisplay gefüllt)
        vertexDeformations: new Float32Array((data.vertexCount || 1) * 3),
        // Rotation pro Buchstabe
        rotationY: 0,
      });

      globalIndex++;
    });
  });

  console.log(`[WordDisplay] Created ${letterInstances.length} letter instances in ${numLines} lines`);
}

/**
 * Erstellt das Haar-System für einen einzelnen Buchstaben
 */
function createHairSystemForLetter(data, xOffset, yOffset = 0) {
  const totalVertices = HAIR_PER_LETTER * SEGMENTS_PER_HAIR;

  const positions = new Float32Array(totalVertices * 3);
  const rootPositions = new Float32Array(totalVertices * 3);
  const rootNormals = new Float32Array(totalVertices * 3);
  const velocities = new Float32Array(totalVertices * 3);
  const segments = new Float32Array(totalVertices);
  const randomOffsets = new Float32Array(totalVertices);
  const lengthMultipliers = new Float32Array(totalVertices);
  const indices = [];

  for (let hair = 0; hair < HAIR_PER_LETTER; hair++) {
    const randomOffset = Math.random();
    const lengthMult = 0.6 + Math.random() * 0.8;

    // Root-Position aus Punktwolke + X/Y-Offset
    const px = data.positions[hair * 3] * LETTER_SCALE + xOffset;
    const py = data.positions[hair * 3 + 1] * LETTER_SCALE + yOffset;
    const pz = data.positions[hair * 3 + 2] * LETTER_SCALE;

    const nx = data.normals[hair * 3];
    const ny = data.normals[hair * 3 + 1];
    const nz = data.normals[hair * 3 + 2];

    for (let seg = 0; seg < SEGMENTS_PER_HAIR; seg++) {
      const idx = hair * SEGMENTS_PER_HAIR + seg;
      const segmentRatio = seg / (SEGMENTS_PER_HAIR - 1);

      positions[idx * 3] = 0;
      positions[idx * 3 + 1] = 0;
      positions[idx * 3 + 2] = 0;

      rootPositions[idx * 3] = px;
      rootPositions[idx * 3 + 1] = py;
      rootPositions[idx * 3 + 2] = pz;

      rootNormals[idx * 3] = nx;
      rootNormals[idx * 3 + 1] = ny;
      rootNormals[idx * 3 + 2] = nz;

      segments[idx] = segmentRatio;
      randomOffsets[idx] = randomOffset;
      lengthMultipliers[idx] = lengthMult;

      if (seg < SEGMENTS_PER_HAIR - 1) {
        indices.push(idx, idx + 1);
      }
    }
  }

  // Geometrie
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));

  const rootPositionAttr = new THREE.BufferAttribute(rootPositions, 3);
  rootPositionAttr.setUsage(THREE.DynamicDrawUsage);
  geometry.setAttribute("aRootPosition", rootPositionAttr);

  const rootNormalAttr = new THREE.BufferAttribute(rootNormals, 3);
  rootNormalAttr.setUsage(THREE.DynamicDrawUsage);
  geometry.setAttribute("aRootNormal", rootNormalAttr);

  const velocityAttr = new THREE.BufferAttribute(velocities, 3);
  velocityAttr.setUsage(THREE.DynamicDrawUsage);
  geometry.setAttribute("aVelocity", velocityAttr);

  geometry.setAttribute("aSegment", new THREE.BufferAttribute(segments, 1));
  geometry.setAttribute("aRandomOffset", new THREE.BufferAttribute(randomOffsets, 1));
  geometry.setAttribute("aLengthMult", new THREE.BufferAttribute(lengthMultipliers, 1));

  geometry.setIndex(indices);

  // Uniforms
  const uniforms = {
    uTime: { value: 0 },
    uMouseY: { value: 0 },
    uHairLength: { value: HAIR_LENGTH },
    uBendStrength: { value: BEND_STRENGTH },
    uColorRoot: { value: new THREE.Color(HAIR_COLOR_ROOT) },
    uColorTip: { value: new THREE.Color(HAIR_COLOR_TIP) },
    uGravityEnabled: { value: 1.0 },
    uGravityStrength: { value: GRAVITY_STRENGTH },
  };

  // Material
  const material = new THREE.ShaderMaterial({
    vertexShader,
    fragmentShader,
    uniforms,
    transparent: true,
    side: THREE.DoubleSide,
    depthWrite: false,
  });

  // Mesh
  const mesh = new THREE.LineSegments(geometry, material);
  mesh.name = `HairSystem_${xOffset}`;
  wordGroup.add(mesh);

  return {
    mesh,
    geometry,
    uniforms,
    rootPositionAttr,
    rootNormalAttr,
    velocityAttr,
  };
}

/**
 * Sampelt Punkte von einem Mesh
 * Gibt auch hairToVertexMap zurück für Haar-Mesh Synchronisation
 */
function samplePointsFromMesh(mesh, count) {
  const geometry = mesh.geometry;
  const positions = new Float32Array(count * 3);
  const normals = new Float32Array(count * 3);
  const hairToVertexMap = new Int32Array(count); // Mapping Haar -> nächster Vertex

  const posAttr = geometry.attributes.position;
  const normAttr = geometry.attributes.normal;
  const index = geometry.index;

  const triangles = [];
  let totalArea = 0;

  // Dreiecke sammeln
  if (index) {
    const indices = index.array;
    for (let i = 0; i < indices.length; i += 3) {
      const area = calculateTriangleArea(posAttr, indices[i], indices[i + 1], indices[i + 2]);
      totalArea += area;
      triangles.push({
        i0: indices[i],
        i1: indices[i + 1],
        i2: indices[i + 2],
        area,
        cumulativeArea: totalArea,
      });
    }
  } else {
    const vertCount = posAttr.count;
    for (let i = 0; i < vertCount; i += 3) {
      const area = calculateTriangleArea(posAttr, i, i + 1, i + 2);
      totalArea += area;
      triangles.push({
        i0: i,
        i1: i + 1,
        i2: i + 2,
        area,
        cumulativeArea: totalArea,
      });
    }
  }

  if (triangles.length === 0 || totalArea === 0) {
    for (let p = 0; p < count; p++) {
      positions[p * 3] = (Math.random() - 0.5) * 2;
      positions[p * 3 + 1] = (Math.random() - 0.5) * 2;
      positions[p * 3 + 2] = (Math.random() - 0.5) * 0.5;
      normals[p * 3] = 0;
      normals[p * 3 + 1] = 0;
      normals[p * 3 + 2] = 1;
      hairToVertexMap[p] = 0;
    }
    return { positions, normals, hairToVertexMap };
  }

  // Punkte sampeln
  for (let p = 0; p < count; p++) {
    const r = Math.random() * totalArea;
    let tri = triangles[0];

    let low = 0,
      high = triangles.length - 1;
    while (low < high) {
      const mid = Math.floor((low + high) / 2);
      if (triangles[mid].cumulativeArea < r) {
        low = mid + 1;
      } else {
        high = mid;
      }
    }
    tri = triangles[low];

    let u = Math.random();
    let v = Math.random();
    if (u + v > 1) {
      u = 1 - u;
      v = 1 - v;
    }
    const w = 1 - u - v;

    // Speichere den Vertex mit dem höchsten Gewicht (nächsten Vertex)
    if (w >= u && w >= v) {
      hairToVertexMap[p] = tri.i0;
    } else if (u >= v) {
      hairToVertexMap[p] = tri.i1;
    } else {
      hairToVertexMap[p] = tri.i2;
    }

    const p0 = getVertex(posAttr, tri.i0);
    const p1 = getVertex(posAttr, tri.i1);
    const p2 = getVertex(posAttr, tri.i2);

    // Blender -> Three.js Transformation
    positions[p * 3] = p0.x * w + p1.x * u + p2.x * v;
    positions[p * 3 + 1] = -(p0.z * w + p1.z * u + p2.z * v);
    positions[p * 3 + 2] = p0.y * w + p1.y * u + p2.y * v;

    if (normAttr) {
      const n0 = getVertex(normAttr, tri.i0);
      const n1 = getVertex(normAttr, tri.i1);
      const n2 = getVertex(normAttr, tri.i2);

      let nx = n0.x * w + n1.x * u + n2.x * v;
      let ny = -(n0.z * w + n1.z * u + n2.z * v);
      let nz = n0.y * w + n1.y * u + n2.y * v;

      const len = Math.sqrt(nx * nx + ny * ny + nz * nz);
      if (len > 0) {
        nx /= len;
        ny /= len;
        nz /= len;
      }

      normals[p * 3] = nx;
      normals[p * 3 + 1] = ny;
      normals[p * 3 + 2] = nz;
    } else {
      normals[p * 3] = 0;
      normals[p * 3 + 1] = 0;
      normals[p * 3 + 2] = 1;
    }
  }

  return { positions, normals, hairToVertexMap };
}

function getVertex(attr, index) {
  return {
    x: attr.getX(index),
    y: attr.getY(index),
    z: attr.getZ(index),
  };
}

function calculateTriangleArea(posAttr, i0, i1, i2) {
  const p0 = getVertex(posAttr, i0);
  const p1 = getVertex(posAttr, i1);
  const p2 = getVertex(posAttr, i2);

  const ax = p1.x - p0.x,
    ay = p1.y - p0.y,
    az = p1.z - p0.z;
  const bx = p2.x - p0.x,
    by = p2.y - p0.y,
    bz = p2.z - p0.z;

  const cx = ay * bz - az * by;
  const cy = az * bx - ax * bz;
  const cz = ax * by - ay * bx;

  return 0.5 * Math.sqrt(cx * cx + cy * cy + cz * cz);
}

/**
 * Update-Funktion (jeden Frame)
 */
export function updateWordDisplay(elapsedTime, deltaTime) {
  if (!wordGroup || !isLoaded) return;

  const now = Date.now() / 300;
  const dt = Math.max(deltaTime, 0.001);

  // Rotation (individuelle Buchstaben-Rotation)
  if (settings.rotation) {
    const rotationDelta = rotationImpulse + settings.rotationSpeed * deltaTime;
    decayRotationImpulse();

    // Jeder Buchstabe rotiert individuell
    letterInstances.forEach((instance) => {
      instance.rotationY += rotationDelta;

      // Mesh rotiert um eigene Position (funktioniert automatisch, da position.x gesetzt ist)
      if (instance.mesh) {
        instance.mesh.rotation.y = instance.rotationY;
      }
      // Haare werden manuell rotiert (siehe unten bei Root-Position Update)
    });
  }

  // Update jedes Haar-System
  letterInstances.forEach((instance) => {
    if (instance.uniforms) {
      instance.uniforms.uTime.value = elapsedTime;
    }

    // Vertex-Deformationen berechnen (für Mesh UND Haare)
    let hasDeformation = false;

    if (instance.positionClone && instance.normalClone && settings.vertices && settings.mouseDeform) {
      let verticesIntensity = settings.verticesIntensity;
      if (settings.mouseIntensity) {
        verticesIntensity = mapY;
      }

      const vertCount = instance.vertexCount;

      // Berechne Deformationen für jeden Vertex
      for (let i = 0; i < vertCount; i++) {
        const ox = instance.positionClone[i * 3];
        const oy = instance.positionClone[i * 3 + 1];
        const oz = instance.positionClone[i * 3 + 2];

        const uX = (ox * 2.0 + oy * 0.5) * Math.PI * AMOUNT * 0.5;
        const uY = (oz * 2.0 + oy * 0.5) * Math.PI * AMOUNT * 0.5;

        const xsin = Math.sin(uX + now) * DAMPING;
        const ycos = Math.cos(uY + now) * DAMPING;
        const waveOffset = (xsin + ycos) * 0.1 * verticesIntensity;

        // Normale für Auslenkungsrichtung
        const nx = instance.normalClone ? instance.normalClone[i * 3] : 0;
        const ny = instance.normalClone ? instance.normalClone[i * 3 + 1] : 1;
        const nz = instance.normalClone ? instance.normalClone[i * 3 + 2] : 0;

        // Deformation speichern (skaliert für Haare)
        instance.vertexDeformations[i * 3] = nx * waveOffset;
        instance.vertexDeformations[i * 3 + 1] = ny * waveOffset;
        instance.vertexDeformations[i * 3 + 2] = nz * waveOffset;

        // Mesh aktualisieren (wenn sichtbar)
        if (instance.mesh) {
          const geometry = instance.mesh.geometry;
          geometry.attributes.position.array[i * 3] = ox + instance.vertexDeformations[i * 3];
          geometry.attributes.position.array[i * 3 + 1] = oy + instance.vertexDeformations[i * 3 + 1];
          geometry.attributes.position.array[i * 3 + 2] = oz + instance.vertexDeformations[i * 3 + 2];
        }
      }

      if (instance.mesh) {
        instance.mesh.geometry.attributes.position.needsUpdate = true;
        instance.mesh.geometry.computeVertexNormals();
      }

      hasDeformation = true;
    }

    // Haare mit Mesh-Deformation synchronisieren (nur wenn Haare aktiviert)
    if (HAIR_ENABLED && instance.rootPositionAttr && instance.hairToVertexMap) {
      const rootPos = instance.rootPositionAttr.array;
      const rootNorm = instance.rootNormalAttr.array;
      const velArray = instance.velocityAttr.array;

      // Rotation um Y-Achse (für diesen Buchstaben)
      const cosR = Math.cos(instance.rotationY);
      const sinR = Math.sin(instance.rotationY);

      for (let hair = 0; hair < HAIR_PER_LETTER; hair++) {
        // Basis-Position aus Original-Positionen (skaliert, OHNE offset zuerst)
        let localX = instance.positions[hair * 3] * LETTER_SCALE;
        let localY = instance.positions[hair * 3 + 1] * LETTER_SCALE;
        let localZ = instance.positions[hair * 3 + 2] * LETTER_SCALE;

        // Wenn Deformation aktiv, addiere die Deformation des zugehörigen Vertex
        if (hasDeformation && instance.hairToVertexMap) {
          const vertexIdx = instance.hairToVertexMap[hair];
          if (vertexIdx >= 0 && vertexIdx * 3 + 2 < instance.vertexDeformations.length) {
            // Deformation skaliert anwenden
            localX += instance.vertexDeformations[vertexIdx * 3] * LETTER_SCALE;
            localY += instance.vertexDeformations[vertexIdx * 3 + 1] * LETTER_SCALE;
            localZ += instance.vertexDeformations[vertexIdx * 3 + 2] * LETTER_SCALE;
          }
        }

        // Rotation um Y-Achse (im lokalen Raum des Buchstabens)
        const rotatedX = localX * cosR + localZ * sinR;
        const rotatedZ = -localX * sinR + localZ * cosR;

        // Finale Position mit Offset (X und Y)
        const px = rotatedX + instance.xOffset;
        const py = localY + (instance.yOffset || 0);
        const pz = rotatedZ;

        // Normale auch rotieren
        const origNx = instance.normals[hair * 3];
        const origNy = instance.normals[hair * 3 + 1];
        const origNz = instance.normals[hair * 3 + 2];
        const nx = origNx * cosR + origNz * sinR;
        const ny = origNy;
        const nz = -origNx * sinR + origNz * cosR;

        // Velocity berechnen
        const lastX = instance.lastRootPositions[hair * 3];
        const lastY = instance.lastRootPositions[hair * 3 + 1];
        const lastZ = instance.lastRootPositions[hair * 3 + 2];

        let velX = (px - lastX) / dt;
        let velY = (py - lastY) / dt;
        let velZ = (pz - lastZ) / dt;

        // Velocity dämpfen
        const dampFactor = 0.7;
        instance.hairVelocities[hair * 3] = instance.hairVelocities[hair * 3] * dampFactor + velX * (1 - dampFactor);
        instance.hairVelocities[hair * 3 + 1] = instance.hairVelocities[hair * 3 + 1] * dampFactor + velY * (1 - dampFactor);
        instance.hairVelocities[hair * 3 + 2] = instance.hairVelocities[hair * 3 + 2] * dampFactor + velZ * (1 - dampFactor);

        // Speichern für nächsten Frame
        instance.lastRootPositions[hair * 3] = px;
        instance.lastRootPositions[hair * 3 + 1] = py;
        instance.lastRootPositions[hair * 3 + 2] = pz;

        // Für alle Segmente des Haars
        for (let seg = 0; seg < SEGMENTS_PER_HAIR; seg++) {
          const idx = hair * SEGMENTS_PER_HAIR + seg;

          rootPos[idx * 3] = px;
          rootPos[idx * 3 + 1] = py;
          rootPos[idx * 3 + 2] = pz;

          rootNorm[idx * 3] = nx;
          rootNorm[idx * 3 + 1] = ny;
          rootNorm[idx * 3 + 2] = nz;

          velArray[idx * 3] = instance.hairVelocities[hair * 3];
          velArray[idx * 3 + 1] = instance.hairVelocities[hair * 3 + 1];
          velArray[idx * 3 + 2] = instance.hairVelocities[hair * 3 + 2];
        }
      }

      instance.rootPositionAttr.needsUpdate = true;
      instance.rootNormalAttr.needsUpdate = true;
      instance.velocityAttr.needsUpdate = true;
    }
  });
}

/**
 * Setzt die Haar-Farben
 */
export function setHairColors(rootColor, tipColor) {
  if (rootColor) {
    HAIR_COLOR_ROOT = rootColor;
    settings.wordDisplayHairColorRoot = rootColor;
  }
  if (tipColor) {
    HAIR_COLOR_TIP = tipColor;
    settings.wordDisplayHairColorTip = tipColor;
  }

  letterInstances.forEach((instance) => {
    if (instance.uniforms) {
      if (rootColor) instance.uniforms.uColorRoot.value = new THREE.Color(rootColor);
      if (tipColor) instance.uniforms.uColorTip.value = new THREE.Color(tipColor);
    }
  });
}

/**
 * Setzt die Mesh-Farbe
 */
export function setMeshColor(color) {
  settings.wordDisplayMeshColor = color;
  const threeColor = new THREE.Color(color);

  letterInstances.forEach((instance) => {
    if (instance.mesh && instance.mesh.material) {
      instance.mesh.material.color = threeColor;
    }
  });
}

/**
 * Setzt Haar-Länge
 */
export function setHairLength(length) {
  HAIR_LENGTH = length;
  settings.wordDisplayHairLength = length;

  letterInstances.forEach((instance) => {
    if (instance.uniforms) {
      instance.uniforms.uHairLength.value = length;
    }
  });
}

/**
 * Cleanup
 */
export function removeWordDisplay() {
  if (wordGroup) {
    scene.remove(wordGroup);

    letterInstances.forEach((instance) => {
      if (instance.hairGeometry) instance.hairGeometry.dispose();
      if (instance.hairMesh && instance.hairMesh.material) instance.hairMesh.material.dispose();
      if (instance.mesh) {
        if (instance.mesh.geometry) instance.mesh.geometry.dispose();
        if (instance.mesh.material) instance.mesh.material.dispose();
      }
    });

    wordGroup = null;
    letterInstances = [];
  }
  isLoaded = false;
}

/**
 * Getter
 */
export function getWordGroup() {
  return wordGroup;
}

export function isWordDisplayLoaded() {
  return isLoaded;
}

export function getCurrentWord() {
  return currentWord;
}

/**
 * Passt die Kamera bei Resize an (für responsive Darstellung)
 */
export function adjustWordDisplayCamera() {
  if (isLoaded && wordWidth > 0) {
    adjustCameraForWidth(wordWidth);
  }
}
